/**
 * User Analytics — Uses BNova backend overview API (PostgreSQL/Prisma).
 * Replaces the legacy page that called non-existent /api/user-analytics/stats.
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Skeleton from "@mui/material/Skeleton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import LocationAnalytics from "components/LocationAnalytics";

// Services
import { getUserStats, getTrends, getOverview } from "services/dashboardService";

const formatUsd = (value) => `$${Number(value || 0).toFixed(4)}`;
const formatTokens = (value) => Number(value || 0).toLocaleString();

const glassCardSx = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "16px",
  transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
  },
};

function UserAnalytics() {
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalInvitedUsers: 0,
  });
  const [trends, setTrends] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stats, trendsData, overview] = await Promise.all([
          getUserStats(),
          getTrends(30),
          getOverview(),
        ]);
        setUserStats(stats);
        setTrends(trendsData);
        setTokenUsage(overview?.tokenUsage || null);
      } catch (err) {
        console.error("User analytics fetch failed:", err);
        setError(err.response?.data?.message || "Failed to load user analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const userAnalyticsChartData = {
    labels: ["Total Users", "Invited Users", "Active Users", "Inactive Users"],
    datasets: {
      label: "Users",
      data: [
        userStats.totalUsers,
        userStats.totalInvitedUsers,
        userStats.activeUsers,
        userStats.inactiveUsers,
      ],
    },
  };

  const signupsChart = trends?.signups
    ? { labels: trends.signups.labels, datasets: { label: "New Users", data: trends.signups.data } }
    : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {error && (
          <MDBox mb={3}>
            <Card sx={{ ...glassCardSx, p: 3, border: "1px solid rgba(244,67,54,0.3)" }}>
              <MDTypography variant="body2" color="error">
                {error}
              </MDTypography>
            </Card>
          </MDBox>
        )}

        {/* Stat cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  height={130}
                  sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.05)" }}
                />
              ) : (
                <ComplexStatisticsCard
                  color="dark"
                  icon="group"
                  title="Total Users"
                  count={userStats.totalUsers}
                  percentage={{ color: "success", amount: "", label: "From PostgreSQL" }}
                />
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  height={130}
                  sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.05)" }}
                />
              ) : (
                <ComplexStatisticsCard
                  icon="person_add"
                  title="Total Invited Users"
                  count={userStats.totalInvitedUsers}
                  percentage={{ color: "success", amount: "", label: "Waitlist total" }}
                />
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  height={130}
                  sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.05)" }}
                />
              ) : (
                <ComplexStatisticsCard
                  color="success"
                  icon="how_to_reg"
                  title="Active Users"
                  count={userStats.activeUsers}
                  percentage={{ color: "success", amount: "", label: "Active in last 30 days" }}
                />
              )}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {loading ? (
                <Skeleton
                  variant="rectangular"
                  height={130}
                  sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.05)" }}
                />
              ) : (
                <ComplexStatisticsCard
                  color="primary"
                  icon="person_remove"
                  title="Inactive Users"
                  count={userStats.inactiveUsers}
                  percentage={{ color: "error", amount: "", label: "Inactive for 30 days" }}
                />
              )}
            </MDBox>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={0.5}>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="payments"
                title="Platform AI Spend (30d)"
                count={
                  tokenUsage ? formatUsd(tokenUsage.total_cost) : loading ? "..." : formatUsd(0)
                }
                percentage={{
                  color: "info",
                  amount: tokenUsage ? formatTokens(tokenUsage.total_tokens) : "0",
                  label: "Total tokens",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="receipt_long"
                title="Avg Cost / Session"
                count={
                  tokenUsage
                    ? formatUsd(tokenUsage.avg_cost_per_session)
                    : loading
                    ? "..."
                    : formatUsd(0)
                }
                percentage={{
                  color: "warning",
                  amount: tokenUsage ? String(tokenUsage.query_count || 0) : "0",
                  label: "Tracked queries",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="calculate"
                title="Avg Cost / Query"
                count={
                  tokenUsage
                    ? formatUsd(tokenUsage.avg_cost_per_query)
                    : loading
                    ? "..."
                    : formatUsd(0)
                }
                percentage={{
                  color: "success",
                  amount: tokenUsage ? formatTokens(tokenUsage.completion_tokens) : "0",
                  label: "Completion tokens",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Location analytics */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <LocationAnalytics />
            </Grid>
          </Grid>
        </MDBox>

        {/* Charts */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="User Analytics"
                  description="Summary of user statistics from PostgreSQL"
                  date="just updated"
                  chart={userAnalyticsChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={3}>
                {signupsChart ? (
                  <ReportsBarChart
                    color="primary"
                    title="User Signups (30 days)"
                    description="New user registrations trend"
                    date="updated just now"
                    chart={signupsChart}
                  />
                ) : (
                  <Card sx={{ ...glassCardSx, p: 3, height: 250 }}>
                    <MDTypography variant="body2" color="text">
                      {loading ? "Loading trends..." : "No signup trend data available."}
                    </MDTypography>
                  </Card>
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserAnalytics;
